import { z } from "zod";
import { AudienceSchema } from "./audience";
import { META_DEFAULTS } from "./defaults";
import { PlacementsSchema } from "./placements";

/**
 * How a goal's Meta ads are set up (GoalConfig.channels.meta.settings). Every field is the goal's own
 * and editable; what is missing falls back to META_DEFAULTS. Who sees the ads is the audience: there is
 * no default place, so a goal cannot launch until it has one (notes/AUDIENCE.md 3).
 */
/** One question on Meta's form: typed when it has no answers, else a pick of them. */
export const FormQuestionSchema = z.object({
  q: z.string().trim().min(1).max(150),
  answers: z.array(z.string().trim().min(1).max(80)).max(10).default([]),
});

/** Meta's own form (notes/AUDIENCE.md 8.1, 9.4): the owner's questions, asked before name, phone and email. */
export const MetaFormSchema = z.object({
  questions: z.array(FormQuestionSchema).max(5).default([]),
  /** Meta shows every form with a link to the business's privacy policy. */
  privacyUrl: z.string().url().max(500).optional(),
});
export type MetaForm = z.infer<typeof MetaFormSchema>;

export const MetaSettingsSchema = z.object({
  /** The page the ads send people to. Missing: the webinar page of the results connection. */
  link: z.string().url().optional(),
  /** The daily amount the campaign starts at. Missing: the goal's daily maximum. */
  dailyCents: z.number().int().positive().optional(),
  audience: AudienceSchema.optional(),
  /** Where the ads show: the spots the owner chose. Missing: Automatic, Meta picks (specs/formal/placements). */
  placements: PlacementsSchema.optional(),
  /** Where the ads open: a page on the business's website, or Meta's own form. */
  opens: z.enum(["site", "metaform"]).default("site"),
  form: MetaFormSchema.optional(),
  /** The event in the dataset the goal counts as its result, by its Meta name (from the plan). */
  counts: z.string().min(1).max(100).optional(),
  callToAction: z.string().min(1).default(META_DEFAULTS.callToAction),
  /** What Meta aims the ads at: its goal, and the pixel event that counts as a result. */
  optimise: z
    .object({
      goal: z.enum(["lead_conversion", "conversions", "traffic"]),
      event: z.string().min(1),
      /** The exact custom event name, when event is OTHER. */
      eventStr: z.string().min(1).optional(),
    })
    .default({ ...META_DEFAULTS.optimise }),
});
export type MetaSettings = z.infer<typeof MetaSettingsSchema>;

export const metaSettings = (settings: Record<string, unknown> | undefined): MetaSettings => MetaSettingsSchema.parse(settings ?? {});

/** The goals that aim at a pixel event, and so need a pixel on the connection. */
export const needsPixel = (s: MetaSettings) => s.opens !== "metaform" && s.optimise.goal !== "traffic";

/**
 * The files a Meta ad can carry, as Flashadz sends them: Meta asks for JPEG or PNG images (Zernio's docs
 * quote Meta: "saved as JPG or PNG"), and MP4 or MOV video, the formats Meta's ad specs name.
 */
export const META_MEDIA_TYPES = ["video/mp4", "video/quicktime", "image/jpeg", "image/png"] as const;
export const metaTakes = (contentType: string | undefined) => (META_MEDIA_TYPES as readonly string[]).includes(contentType ?? "");
